import React, { useEffect, useRef, useState } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import { IconButton } from './ui';
import docshawkService, { SourceDocument } from '../services/docshawkService';
import '../styles/theme.css';

interface Props {
  pattern: string;
  onChangePattern: (p: string) => void;
  documents: SourceDocument[];
  onClose: () => void;
}

const SAMPLE = 'This LOAN AGREEMENT dated as of April 20, 2020 among BR Holdings, LLC, as Borrower, '
  + 'and the United States Department of the Treasury, as Lender. Consolidated Leverage Ratio '
  + 'not to exceed 3.50:1.00. Governed by the laws of the State of New York.';

const RegexTester: React.FC<Props> = ({ pattern, onChangePattern, documents, onClose }) => {
  const [mode, setMode] = useState<'sample' | 'document' | 'paste'>(documents.length ? 'document' : 'sample');
  const [documentId, setDocumentId] = useState(documents[0]?.id || '');
  const [pasted, setPasted] = useState('');
  const [result, setResult] = useState<{ matches: any[]; count: number; error: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef<any>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!pattern) { setResult(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const opts = mode === 'document' ? { documentId }
          : { text: mode === 'paste' ? pasted : SAMPLE };
        setResult(await docshawkService.testRegex(pattern, opts));
      } catch { setResult({ matches: [], count: 0, error: 'test failed' }); }
      finally { setLoading(false); }
    }, 400);
    return () => timer.current && clearTimeout(timer.current);
  }, [pattern, mode, documentId, pasted]);

  return (
    <div className="mx-card" style={{ borderColor: 'var(--mx-accent)', marginTop: 6 }}>
      <div className="mx-card-head" style={{ background: 'var(--mx-accent-weak)' }}>
        <h3 className="mx-card-title">Regex tester</h3>
        <IconButton icon={X} label="Close" onClick={onClose} />
      </div>
      <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input className="mx-input mx-mono" style={{ fontSize: 13 }} placeholder="regex pattern — capture group 1 is the value"
          value={pattern} onChange={(e) => onChangePattern(e.target.value)} />

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {(['document', 'sample', 'paste'] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} disabled={m === 'document' && !documents.length}
              className={`mx-btn mx-btn-sm ${mode === m ? 'mx-btn-primary' : 'mx-btn-outline'}`}>
              {m === 'document' ? 'a document' : m === 'sample' ? 'sample text' : 'paste text'}</button>
          ))}
          {mode === 'document' && (
            <select className="mx-select" style={{ width: 'auto', flex: 1, minWidth: 160 }}
              value={documentId} onChange={(e) => setDocumentId(e.target.value)}>
              {documents.map((d) => <option key={d.id} value={d.id}>{d.filename}</option>)}
            </select>
          )}
        </div>
        {mode === 'paste' && (
          <textarea className="mx-input mx-mono" style={{ fontSize: 12, minHeight: 70 }}
            placeholder="paste document text…" value={pasted} onChange={(e) => setPasted(e.target.value)} />
        )}

        {result?.error ? (
          <div style={{ color: 'var(--mx-danger)', fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
            <AlertTriangle size={14} /> {result.error}</div>
        ) : result ? (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: result.count ? 'var(--mx-success)' : 'var(--mx-text-muted)',
              display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              {result.count ? <Check size={14} /> : null}
              {result.count} match{result.count === 1 ? '' : 'es'}{loading ? ' …' : ''}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
              {result.matches.map((m, i) => (
                <div key={i} style={{ fontSize: 12.5 }}>
                  <span style={{ fontWeight: 600 }}>{m.value || '(empty)'}</span>
                  <span className="mx-subtle" style={{ marginLeft: 8 }}>…{m.context}…</span>
                </div>
              ))}
            </div>
          </div>
        ) : <div className="mx-subtle" style={{ fontSize: 12 }}>Enter a pattern to see matches.</div>}
      </div>
    </div>
  );
};

export default RegexTester;
